import pageTitle, { SITE_SUFFIX, TITLE_MAX } from './seo-title.js';

// Yoast-style checks for a Storify story. Pure functions, so they run the same
// in the editor and in tests. Statuses: good (green), ok (amber), bad (red),
// info (grey: worth knowing, nothing to fix).

// The page title is "<SEO title> | Newspage", and Google shows about 60 characters.
export const SEO_TITLE_MAX = TITLE_MAX - SITE_SUFFIX.length - 1;
export const META_MIN = 120;
export const META_MAX = 156;
// Google News shows up to about 110 characters of a headline.
export const HEADLINE_MAX = 110;
export const MIN_WORDS = 300;
export const LONG_SENTENCE_WORDS = 20;
export const LONG_PARAGRAPH_WORDS = 150;
export const HOST_NAME = 'app.newspage.media';

const ENTITIES = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
    '&#039;': "'",
    '&apos;': "'",
    '&lsquo;': '‘',
    '&rsquo;': '’',
    '&ldquo;': '“',
    '&rdquo;': '”',
    '&ndash;': '–',
    '&mdash;': '—',
    '&pound;': '£',
    '&lt;': '<',
    '&gt;': '>',
};

export const stripHtml = (html = '') =>
    String(html || '')
        .replace(/<(br|\/p|\/h[1-6]|\/li|\/blockquote)[^>]*>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&[a-z#0-9]+;/gi, (entity) => ENTITIES[entity] ?? ' ')
        .replace(/\s+/g, ' ')
        .trim();

// Lower case, curly quotes straightened, punctuation removed (but not inside
// words: "buy-to-let" and "lender's" stay whole).
export const normalise = (text = '') =>
    String(text || '')
        .toLowerCase()
        .replace(/[‘’`]/g, "'")
        .replace(/[^\p{L}\p{N}'£$%-]+/gu, ' ')
        .replace(/(^|\s)['-]+|['-]+(?=\s|$)/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

export const words = (text = '') => normalise(text).split(' ').filter(Boolean);

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Each word of the keyphrase also matches its plural or possessive
// ("fractional CFO" matches "fractional CFOs").
const wordPattern = (word) => `${escapeRegExp(word)}(?:'s|s|es)?`;

// Occurrences of the keyphrase as a whole phrase.
export const countPhrase = (text, phrase) => {
    const parts = words(phrase);
    if (!parts.length) return 0;
    const re = new RegExp(
        `(^|\\s)${parts.map(wordPattern).join('\\s')}(?=\\s|$)`,
        'g'
    );
    return (normalise(text).match(re) || []).length;
};

// 'exact' when the phrase appears as written, 'words' when every word of it
// appears somewhere (Yoast counts that as a match too), otherwise 'none'.
export const matchKeyphrase = (text, phrase) => {
    const parts = words(phrase);
    if (!parts.length) return 'none';
    if (countPhrase(text, phrase) > 0) return 'exact';
    const haystack = normalise(text);
    return parts.every((w) =>
        new RegExp(`(^|\\s)${wordPattern(w)}(?=\\s|$)`).test(haystack)
    )
        ? 'words'
        : 'none';
};

export const paragraphs = (html = '') => {
    const found = [];
    const re = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    let match;
    while ((match = re.exec(html || ''))) {
        const text = stripHtml(match[1]);
        if (text) found.push(text);
    }
    if (!found.length) {
        const text = stripHtml(html);
        if (text) found.push(text);
    }
    return found;
};

const ABBREVIATION = /\b(Mr|Mrs|Ms|Dr|Prof|St|vs|etc|No|e\.g|i\.e)\.$/i;

export const sentences = (text = '') =>
    String(text || '')
        .split(/(?<=[.!?])\s+(?=[“"‘'(]?[A-Z0-9£$])/)
        .reduce((all, piece) => {
            const last = all[all.length - 1];
            if (last && ABBREVIATION.test(last)) {
                all[all.length - 1] = `${last} ${piece}`;
            } else {
                all.push(piece);
            }
            return all;
        }, [])
        .map((s) => s.trim())
        .filter((s) => words(s).length > 0);

const links = (html = '') => {
    const hrefs = [];
    const re = /<a\s[^>]*href=["']([^"']+)["']/gi;
    let match;
    while ((match = re.exec(html || ''))) hrefs.push(match[1]);
    return hrefs;
};

const isInternal = (href) =>
    href.startsWith('/') ||
    /^https?:\/\/([a-z0-9-]+\.)*newspage\.(media|news)(\/|$)/i.test(href);

export const excerptFrom = (html, max = META_MAX) => {
    const text = stripHtml(html);
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
};

const check = (id, group, label, status, text) => ({
    id,
    group,
    label,
    status,
    text,
});

export function storySeoChecks(story = {}, { slug = '' } = {}) {
    const headline = (story.headline || '').trim();
    const seoTitle = (story.seoTitle || '').trim() || headline;
    const meta = (story.metaDescription || '').trim();
    const keyphrase = (story.focusKeyphrase || '').trim();
    const body = story.body || '';
    const text = stripHtml(body);
    const wordCount = words(text).length;
    const paras = paragraphs(body);
    const hasKeyphrase = words(keyphrase).length > 0;
    const checks = [];

    // SEO
    const kpWords = words(keyphrase).length;
    checks.push(
        !hasKeyphrase
            ? check(
                  'keyphrase',
                  'seo',
                  'Focus keyphrase',
                  'bad',
                  'Set the search you want this story to rank for.'
              )
            : kpWords > 4
            ? check(
                  'keyphrase',
                  'seo',
                  'Focus keyphrase',
                  'ok',
                  `${kpWords} words is long. Most searches are 1 to 4 words.`
              )
            : check('keyphrase', 'seo', 'Focus keyphrase', 'good', 'Set.')
    );

    if (hasKeyphrase) {
        const inTitle = matchKeyphrase(seoTitle, keyphrase);
        const atStart = normalise(seoTitle).startsWith(normalise(keyphrase));
        checks.push(
            inTitle === 'none'
                ? check(
                      'kp-title',
                      'seo',
                      'Keyphrase in SEO title',
                      'bad',
                      'Put the keyphrase in the SEO title, ideally at the start.'
                  )
                : atStart
                ? check(
                      'kp-title',
                      'seo',
                      'Keyphrase in SEO title',
                      'good',
                      'At the start of the title.'
                  )
                : check(
                      'kp-title',
                      'seo',
                      'Keyphrase in SEO title',
                      'ok',
                      'In the title. Moving it to the start helps.'
                  )
        );

        checks.push(
            matchKeyphrase(headline, keyphrase) === 'none'
                ? check(
                      'kp-headline',
                      'seo',
                      'Keyphrase in headline',
                      'ok',
                      'The headline (H1) does not contain the keyphrase.'
                  )
                : check(
                      'kp-headline',
                      'seo',
                      'Keyphrase in headline',
                      'good',
                      'The headline contains it.'
                  )
        );

        checks.push(
            !meta
                ? check(
                      'kp-meta',
                      'seo',
                      'Keyphrase in meta description',
                      'bad',
                      'Write a meta description that uses the keyphrase.'
                  )
                : matchKeyphrase(meta, keyphrase) === 'none'
                ? check(
                      'kp-meta',
                      'seo',
                      'Keyphrase in meta description',
                      'bad',
                      'The meta description does not use the keyphrase.'
                  )
                : check(
                      'kp-meta',
                      'seo',
                      'Keyphrase in meta description',
                      'good',
                      'Used.'
                  )
        );

        const intro = paras[0] || '';
        checks.push(
            matchKeyphrase(intro, keyphrase) === 'none'
                ? check(
                      'kp-intro',
                      'seo',
                      'Keyphrase in introduction',
                      'bad',
                      'Use the keyphrase in the first paragraph.'
                  )
                : check(
                      'kp-intro',
                      'seo',
                      'Keyphrase in introduction',
                      'good',
                      'The first paragraph uses it.'
                  )
        );

        const occurrences = countPhrase(text, keyphrase);
        const density = wordCount
            ? (occurrences * kpWords * 100) / wordCount
            : 0;
        const used = `Used ${occurrences} time${
            occurrences === 1 ? '' : 's'
        } (${density.toFixed(1)}%)`;
        checks.push(
            wordCount < 100
                ? check(
                      'kp-density',
                      'seo',
                      'Keyphrase density',
                      'info',
                      'Counts once the story passes 100 words.'
                  )
                : density > 3
                ? check(
                      'kp-density',
                      'seo',
                      'Keyphrase density',
                      'bad',
                      `${used}. That reads as stuffing: use it less.`
                  )
                : density < 0.5
                ? check(
                      'kp-density',
                      'seo',
                      'Keyphrase density',
                      'ok',
                      `${used}. Aim for 0.5–3%.`
                  )
                : check(
                      'kp-density',
                      'seo',
                      'Keyphrase density',
                      'good',
                      `${used}.`
                  )
        );

        const slugText = String(slug || '').replace(/-/g, ' ');
        checks.push(
            matchKeyphrase(slugText, keyphrase) === 'none'
                ? check(
                      'kp-slug',
                      'seo',
                      'Keyphrase in URL',
                      'info',
                      'Not in the URL. The URL stays as it is: it is already indexed, and changing it would lose that.'
                  )
                : check(
                      'kp-slug',
                      'seo',
                      'Keyphrase in URL',
                      'good',
                      'The URL contains it.'
                  )
        );
    }

    checks.push(
        !seoTitle
            ? check(
                  'title-length',
                  'seo',
                  'SEO title width',
                  'bad',
                  'Write a headline or an SEO title.'
              )
            : seoTitle.length > SEO_TITLE_MAX
            ? check(
                  'title-length',
                  'seo',
                  'SEO title width',
                  'ok',
                  `${seoTitle.length} characters. Over ${SEO_TITLE_MAX}, Google cuts it off (the page adds "${SITE_SUFFIX}").`
              )
            : seoTitle.length < 30
            ? check(
                  'title-length',
                  'seo',
                  'SEO title width',
                  'ok',
                  `${seoTitle.length} characters. There is room for more.`
              )
            : check(
                  'title-length',
                  'seo',
                  'SEO title width',
                  'good',
                  `${seoTitle.length} of ${SEO_TITLE_MAX} characters.`
              )
    );

    checks.push(
        !meta
            ? check(
                  'meta-length',
                  'seo',
                  'Meta description length',
                  'bad',
                  'Empty, so Google will pick its own text.'
              )
            : meta.length < META_MIN
            ? check(
                  'meta-length',
                  'seo',
                  'Meta description length',
                  'ok',
                  `${meta.length} characters. Aim for ${META_MIN}–${META_MAX}.`
              )
            : meta.length > META_MAX
            ? check(
                  'meta-length',
                  'seo',
                  'Meta description length',
                  'ok',
                  `${meta.length} characters. Google cuts it off after about ${META_MAX}.`
              )
            : check(
                  'meta-length',
                  'seo',
                  'Meta description length',
                  'good',
                  `${meta.length} characters.`
              )
    );

    checks.push(
        !headline
            ? check(
                  'headline',
                  'seo',
                  'Headline',
                  'bad',
                  'The story needs a headline.'
              )
            : headline.length > HEADLINE_MAX
            ? check(
                  'headline',
                  'seo',
                  'Headline',
                  'ok',
                  `${headline.length} characters. Google News shows about ${HEADLINE_MAX}.`
              )
            : check(
                  'headline',
                  'seo',
                  'Headline',
                  'good',
                  `${headline.length} characters.`
              )
    );

    checks.push(
        wordCount >= MIN_WORDS
            ? check(
                  'length',
                  'seo',
                  'Text length',
                  'good',
                  `${wordCount} words.`
              )
            : check(
                  'length',
                  'seo',
                  'Text length',
                  wordCount >= 200 ? 'ok' : 'bad',
                  `${wordCount} words. Aim for at least ${MIN_WORDS}.`
              )
    );

    const hrefs = links(body);
    const outbound = hrefs.filter((h) => /^https?:/i.test(h) && !isInternal(h));
    const internal = hrefs.filter(isInternal);
    checks.push(
        outbound.length
            ? check(
                  'outbound',
                  'seo',
                  'Outbound links',
                  'good',
                  `${outbound.length} link${
                      outbound.length === 1 ? '' : 's'
                  } to sources.`
              )
            : check(
                  'outbound',
                  'seo',
                  'Outbound links',
                  'ok',
                  'Link to the research or data the story relies on.'
              )
    );
    checks.push(
        internal.length
            ? check(
                  'internal',
                  'seo',
                  'Internal links',
                  'good',
                  `${internal.length} link${
                      internal.length === 1 ? '' : 's'
                  } to Newspage.`
              )
            : check(
                  'internal',
                  'seo',
                  'Internal links',
                  'ok',
                  "Link to the experts' Newspages or related stories."
              )
    );

    const images = (body || '').match(/<img\b[^>]*>/gi) || [];
    const noAlt = images.filter((img) => !/\balt=["'][^"']+["']/i.test(img));
    if (images.length) {
        checks.push(
            noAlt.length
                ? check(
                      'alt',
                      'seo',
                      'Image alt text',
                      'ok',
                      `${noAlt.length} image${
                          noAlt.length === 1 ? ' has' : 's have'
                      } no alt text.`
                  )
                : check(
                      'alt',
                      'seo',
                      'Image alt text',
                      'good',
                      'Every image has alt text.'
                  )
        );
    }

    // Readability
    const allSentences = paras.flatMap(sentences);
    const long = allSentences.filter(
        (s) => words(s).length > LONG_SENTENCE_WORDS
    );
    const longShare = allSentences.length
        ? (long.length * 100) / allSentences.length
        : 0;
    if (allSentences.length) {
        checks.push(
            longShare > 25
                ? check(
                      'sentences',
                      'readability',
                      'Sentence length',
                      'ok',
                      `${Math.round(
                          longShare
                      )}% of sentences are over ${LONG_SENTENCE_WORDS} words. Keep it under 25%.`
                  )
                : check(
                      'sentences',
                      'readability',
                      'Sentence length',
                      'good',
                      `${Math.round(
                          longShare
                      )}% of sentences are over ${LONG_SENTENCE_WORDS} words.`
                  )
        );
    }

    const longParas = paras.filter(
        (p) => words(p).length > LONG_PARAGRAPH_WORDS
    );
    if (paras.length) {
        checks.push(
            longParas.length
                ? check(
                      'paragraphs',
                      'readability',
                      'Paragraph length',
                      'ok',
                      `${longParas.length} paragraph${
                          longParas.length === 1 ? ' is' : 's are'
                      } over ${LONG_PARAGRAPH_WORDS} words.`
                  )
                : check(
                      'paragraphs',
                      'readability',
                      'Paragraph length',
                      'good',
                      'No paragraph is too long.'
                  )
        );
    }

    if (wordCount > MIN_WORDS) {
        checks.push(
            /<h[2-4][\s>]/i.test(body)
                ? check(
                      'subheadings',
                      'readability',
                      'Subheadings',
                      'good',
                      'The story is broken up with subheadings.'
                  )
                : check(
                      'subheadings',
                      'readability',
                      'Subheadings',
                      'ok',
                      'Add subheadings (Heading 2) to break up a longer story.'
                  )
        );
    }

    return checks;
}

// One traffic light for the whole story, as Yoast shows.
export const overallStatus = (checks) => {
    const scored = checks.filter((c) => c.status !== 'info');
    if (!scored.length) return 'bad';
    if (scored.some((c) => c.status === 'bad')) return 'bad';
    const ok = scored.filter((c) => c.status === 'ok').length;
    return ok > 2 ? 'ok' : 'good';
};

// What the Google result will look like.
export const searchSnippet = (story = {}, slug = '') => ({
    title: pageTitle((story.seoTitle || '').trim() || story.headline || ''),
    url: `${HOST_NAME} › news-alerts › ${slug}`,
    description:
        (story.metaDescription || '').trim() || excerptFrom(story.body),
});
